import { Component, forwardRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DialogueTexts } from '../../constants/dialog-texts.constant';

@Component({
  selector: 'app-date-picker',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatIconModule],
  template: `
    <div class="date-picker-container">
      <button
        type="button"
        class="date-picker-button"
        (click)="togglePicker()"
        [attr.aria-expanded]="showPicker"
      >
        <span class="selected-date" [class.placeholder]="!selectedDate">{{
          displayDate || placeholder
        }}</span>
        <mat-icon>calendar_today</mat-icon>
      </button>

      <div class="date-picker-dropdown" *ngIf="showPicker" (click)="$event.stopPropagation()">
        <div class="picker-header">
          <button type="button" (click)="previousMonth()" class="nav-btn">
            <mat-icon>chevron_left</mat-icon>
          </button>
          <div class="month-year-selector">
            <button type="button" (click)="toggleMonthDropdown()" class="month-year-display">
              {{ months[currentMonth].full }} {{ currentYear }}
              <mat-icon class="dropdown-icon">expand_more</mat-icon>
            </button>
            <div class="month-year-dropdown" *ngIf="showMonthYearDropdown">
              <div class="dropdown-row">
                <div class="year-nav">
                  <button type="button" (click)="dropdownPrevYear()" class="nav-btn-sm">
                    <mat-icon>chevron_left</mat-icon>
                  </button>
                  <span class="year-label">{{ dropdownYear }}</span>
                  <button type="button" (click)="dropdownNextYear()" class="nav-btn-sm">
                    <mat-icon>chevron_right</mat-icon>
                  </button>
                </div>
                <div class="month-grid">
                  <button
                    type="button"
                    *ngFor="let month of months; let i = index"
                    class="month-option"
                    [class.selected]="i === currentMonth && dropdownYear === currentYear"
                    (click)="selectMonthYear(i, dropdownYear)"
                  >
                    {{ month.short }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <button type="button" (click)="nextMonth()" class="nav-btn">
            <mat-icon>chevron_right</mat-icon>
          </button>
        </div>

        <div class="weekday-header">
          <span *ngFor="let day of weekDays">{{ day }}</span>
        </div>

        <div class="days-grid">
          <button
            type="button"
            *ngFor="let day of calendarDays"
            class="day-btn"
            [class.other-month]="!day.currentMonth"
            [class.today]="day.isToday"
            [class.selected]="day.isSelected"
            [class.disabled]="day.disabled"
            [disabled]="day.disabled || !day.currentMonth"
            (click)="day.currentMonth && !day.disabled && selectDay(day)"
          >
            {{ day.day }}
          </button>
        </div>

        <div class="picker-actions">
          <button type="button" class="aecom_btn" (click)="cancel()">Close</button>
          <button
            type="button"
            class="aecom_btn--solid"
            (click)="confirm()"
            [disabled]="!tempDate"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      .date-picker-container {
        position: relative;
        width: 100%;
      }

      .date-picker-button {
        width: 100%;
        height: 40px;
        padding: 10px 12px;
        border: 1px solid #ccc;
        border-radius: 4px;
        background: white;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 14px;
        transition: border-color 0.3s;

        &:hover {
          border-color: #999;
        }

        &:focus {
          border: 2px solid #2a2424;
        }
      }

      .selected-date {
        flex: 1;
        text-align: left;
      }

      .selected-date.placeholder {
        color: #9d9c9c !important;
        font-family: 'AECOMSans';
        font-weight: 400;
        font-size: 14px;
      }

      mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        color: #666;
      }

      .date-picker-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        width: 320px;
        background: white;
        border: 1px solid #ccc;
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        z-index: 1000;
        margin-top: 8px;
        padding: 16px;
      }

      .picker-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
        padding-bottom: 12px;
        border-bottom: 1px solid #eee;
        gap: 8px;
      }

      .month-year-selector {
        position: relative;
        flex: 1;
        text-align: center;
      }

      .month-year-display {
        background: none;
        border: 1px solid #ddd;
        padding: 6px 12px;
        border-radius: 4px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        width: 100%;
        font-weight: 500;
        font-size: 14px;
        transition: all 0.2s;

        &:hover {
          border-color: #008768;
          background-color: #f5f5f5;
        }
      }

      .dropdown-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }

      .month-year-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #ddd;
        border-radius: 4px;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
        z-index: 1001;
        margin-top: 4px;
        padding: 12px;
      }

      .dropdown-row {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .year-nav {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .year-label {
        font-weight: 600;
        font-size: 14px;
      }

      .nav-btn-sm {
        background: none;
        border: none;
        cursor: pointer;
        padding: 2px;
        display: flex;
        align-items: center;
        border-radius: 4px;
        transition: background-color 0.2s;

        &:hover {
          background-color: #f0f0f0;
        }

        mat-icon {
          font-size: 18px;
          width: 18px;
          height: 18px;
        }
      }

      .month-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 4px;
      }

      .month-option {
        padding: 6px 4px;
        border: 1px solid transparent;
        border-radius: 100px;
        background: white;
        cursor: pointer;
        font-size: 12px;
        transition: all 0.2s;

        &:hover {
          border-color: #008768;
          background-color: #f5f5f5;
        }

        &.selected {
          background-color: #008768;
          color: white;
          border-color: #008768;
        }
      }

      .nav-btn {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background-color 0.2s;

        &:hover {
          background-color: #f0f0f0;
        }
      }

      .weekday-header {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        text-align: center;
        margin-bottom: 4px;

        span {
          font-size: 12px;
          font-weight: 600;
          color: #666;
          padding: 4px 0;
        }
      }

      .days-grid {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
        margin-bottom: 16px;
      }

      .day-btn {
        width: 36px;
        height: 36px;
        border: none;
        border-radius: 50%;
        background: white;
        cursor: pointer;
        font-size: 13px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto;
        transition: all 0.2s;

        &:hover:not(.disabled):not(.other-month) {
          background-color: #f5f5f5;
          border: 1px solid #008768;
        }

        &.today {
          font-weight: 700;
          border: 1px solid #008768;
        }

        &.selected {
          background-color: #008768;
          color: white;
          font-weight: 600;
        }

        &.other-month {
          color: #ccc;
          cursor: default;
        }

        &.disabled {
          color: #ccc;
          cursor: not-allowed;
        }
      }

      .picker-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
        padding-top: 12px;
        border-top: 1px solid #eee;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatePickerComponent),
      multi: true,
    },
  ],
})
export class DatePickerComponent implements ControlValueAccessor {
  @Input() placeholder = 'Select Date';
  @Input() minDate: string | null = null;
  @Input() maxDate: string | null = null;

  showPicker = false;
  showMonthYearDropdown = false;
  currentMonth: number = new Date().getMonth();
  currentYear: number = new Date().getFullYear();
  dropdownYear: number = new Date().getFullYear();

  tempDate: string | null = null;
  selectedDate: string | null = null;
  displayDate: string = '';

  weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  months = [
    { short: 'Jan', full: 'January' },
    { short: 'Feb', full: 'February' },
    { short: 'Mar', full: 'March' },
    { short: 'Apr', full: 'April' },
    { short: 'May', full: 'May' },
    { short: 'Jun', full: 'June' },
    { short: 'Jul', full: 'July' },
    { short: 'Aug', full: 'August' },
    { short: 'Sep', full: 'September' },
    { short: 'Oct', full: 'October' },
    { short: 'Nov', full: 'November' },
    { short: 'Dec', full: 'December' },
  ];

  calendarDays: CalendarDay[] = [];

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit() {
    this.generateCalendar();
  }

  togglePicker() {
    this.showPicker = !this.showPicker;
    if (this.showPicker) {
      this.tempDate = this.selectedDate;
      if (this.selectedDate) {
        const [year, month] = this.selectedDate.split('-').map(Number);
        this.currentYear = year;
        this.currentMonth = month - 1;
      }
      this.generateCalendar();
      document.addEventListener('click', this.onClickOutside);
    }
  }

  private onClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.date-picker-container')) {
      this.showPicker = false;
      this.showMonthYearDropdown = false;
      document.removeEventListener('click', this.onClickOutside);
    }
  };

  toggleMonthDropdown() {
    this.showMonthYearDropdown = !this.showMonthYearDropdown;
    this.dropdownYear = this.currentYear;
  }

  dropdownPrevYear() {
    this.dropdownYear--;
  }

  dropdownNextYear() {
    this.dropdownYear++;
  }

  selectMonthYear(month: number, year: number) {
    this.currentMonth = month;
    this.currentYear = year;
    this.showMonthYearDropdown = false;
    this.generateCalendar();
  }

  previousMonth() {
    if (this.currentMonth === 0) {
      this.currentMonth = 11;
      this.currentYear--;
    } else {
      this.currentMonth--;
    }
    this.generateCalendar();
  }

  nextMonth() {
    if (this.currentMonth === 11) {
      this.currentMonth = 0;
      this.currentYear++;
    } else {
      this.currentMonth++;
    }
    this.generateCalendar();
  }

  selectDay(day: CalendarDay) {
    this.tempDate = day.dateStr;
    this.generateCalendar();
  }

  confirm() {
    if (this.tempDate) {
      this.selectedDate = this.tempDate;
      this.updateDisplay();
      this.onChange(this.selectedDate);
      this.showPicker = false;
      document.removeEventListener('click', this.onClickOutside);
    }
  }

  cancel() {
    this.showPicker = false;
    this.showMonthYearDropdown = false;
    this.tempDate = null;
    document.removeEventListener('click', this.onClickOutside);
  }

  private generateCalendar() {
    const firstDay = new Date(this.currentYear, this.currentMonth, 1).getDay();
    const daysInMonth = new Date(this.currentYear, this.currentMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(this.currentYear, this.currentMonth, 0).getDate();

    const today = new Date();
    const todayStr = this.formatDate(today.getFullYear(), today.getMonth() + 1, today.getDate());

    this.calendarDays = [];

    // Previous month days
    for (let i = firstDay - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      this.calendarDays.push({
        day,
        currentMonth: false,
        isToday: false,
        isSelected: false,
        disabled: false,
        dateStr: '',
      });
    }

    // Current month days
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = this.formatDate(this.currentYear, this.currentMonth + 1, d);
      const disabled = this.isDateDisabled(dateStr);
      this.calendarDays.push({
        day: d,
        currentMonth: true,
        isToday: dateStr === todayStr,
        isSelected: dateStr === this.tempDate,
        disabled,
        dateStr,
      });
    }

    // Next month days to fill grid
    const remaining = 42 - this.calendarDays.length;
    for (let i = 1; i <= remaining; i++) {
      this.calendarDays.push({
        day: i,
        currentMonth: false,
        isToday: false,
        isSelected: false,
        disabled: false,
        dateStr: '',
      });
    }
  }

  private isDateDisabled(dateStr: string): boolean {
    if (this.minDate && dateStr < this.minDate) return true;
    if (this.maxDate && dateStr > this.maxDate) return true;
    return false;
  }

  private formatDate(year: number, month: number, day: number): string {
    const m = month.toString().padStart(2, '0');
    const d = day.toString().padStart(2, '0');
    return `${year}-${m}-${d}`;
  }

  private updateDisplay() {
    if (this.selectedDate) {
      const [year, month, day] = this.selectedDate.split('-').map(Number);
      const monthObj = this.months[month - 1];
      this.displayDate = `${monthObj.full} ${day}, ${year}`;
    } else {
      this.displayDate = '';
    }
  }

  writeValue(value: any): void {
    if (value === null || value === '') {
      this.selectedDate = null;
      this.displayDate = '';
      return;
    }
    this.selectedDate = value;
    if (value) {
      const [year, month] = value.split('-').map(Number);
      this.currentYear = year;
      this.currentMonth = month - 1;
    }
    this.updateDisplay();
    this.generateCalendar();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}

interface CalendarDay {
  day: number;
  currentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  disabled: boolean;
  dateStr: string;
}
